/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import axios from 'axios';
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { useEffect, useState } from "react";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import StringEntry from "../datatypeComponents/StringEntry";
import Spinner from "../components/Spinner";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import SimpleResourceFieldViewer from "./SimpleResourceFieldViewer";
import BooleanEntry from "../datatypeComponents/BooleanEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import ReorganizeConceptsButton from "./ReorganizeConceptsModal";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import { Button } from "semantic-ui-react";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserNotificationEmail } from "../slices/currentUser/currentUserSlice";
import server_config from '../ServerConfiguration';

const fevir_node_server_address = server_config["fevir_node_server_address"];

const CodeSystemPropertyEntry = ({ elementName, fieldLabel, startingValue, setResourceState }) => {

    const [codeSystemPropertyState, setCodeSystemPropertyState] = useState({});

    useEffect(() => {
        let startingProperty = { "code": "", "uri": "", "description": "", "type": "" }
        if (startingValue) {
            if (startingValue.extension) { startingProperty.extension = startingValue.extension; }
            if (startingValue.code) { startingProperty.code = startingValue.code; }
            if (startingValue.uri) { startingProperty.uri = startingValue.uri; }
            if (startingValue.description) { startingProperty.description = startingValue.description; }
            if (startingValue.type) { startingProperty.type = startingValue.type; }
        }
        setCodeSystemPropertyState(JSON.parse(JSON.stringify(startingProperty)));
    }, []);

    useEffect((() => {
        if (Object.keys(codeSystemPropertyState).length > 0) {
            let newProperty = {};
            if (codeSystemPropertyState.extension) { newProperty.extension = codeSystemPropertyState.extension; }
            if (codeSystemPropertyState.code) { newProperty.code = codeSystemPropertyState.code; }
            if (codeSystemPropertyState.uri) { newProperty.uri = codeSystemPropertyState.uri; }
            if (codeSystemPropertyState.description) { newProperty.description = codeSystemPropertyState.description; }
            if (codeSystemPropertyState.type) { newProperty.type = codeSystemPropertyState.type; }
            if (Object.keys(newProperty).length === 0) {
                newProperty = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newProperty }; });
        }
    }), [codeSystemPropertyState]);

    if (Object.keys(codeSystemPropertyState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='code' fieldLabel='Code'
                    startingValue={codeSystemPropertyState.code}
                    setResourceState={setCodeSystemPropertyState} />
                <UriEntry elementName='uri' fieldLabel='Formal identifier'
                    startingValue={codeSystemPropertyState.uri}
                    setResourceState={setCodeSystemPropertyState} />
                <StringEntry elementName='description' fieldLabel='Description'
                    startingValue={codeSystemPropertyState.description}
                    setResourceState={setCodeSystemPropertyState} />
                <CodeEntry elementName='type' fieldLabel='Datatype'
                    allowedValues={["code", "Coding", "string", "integer", "boolean", "dateTime", "decimal"]}
                    startingValue={codeSystemPropertyState.type}
                    setResourceState={setCodeSystemPropertyState} />
            </div>
        </>
    }
}

const removeEmptyPropertyValues = (concept) => {
    if (concept.property?.length > 0) {
        concept.property = concept.property.filter((property) => { return Object.keys(property).length !== 1 });
    }
    if (concept.concept?.length > 0) {
        for (let subconcept of concept.concept) {
            removeEmptyPropertyValues(subconcept);
        }
    }
    return concept;
};

const CodeSystemDisplay = ({ foi }) => {
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    const userId = useSelector(selectCurrentUserId);
    const isLoggedIn = (!!userId && userId !== "1");
    const apiToken = useSelector(selectCurrentUserApiToken);
    const notificationEmail = useSelector(selectCurrentUserNotificationEmail);

    const [codeSystemResourceState, setCodeSystemResourceState] = useState({});
    const [reorganizeConceptsModalState, setReorganizeConceptsModalState] = useState({});
    const [conceptsReorganizedState, setConceptsReorganizedState] = useState({});
    const [emptyPropertyValuesClearedState, setEmptyPropertyValuesClearedState] = useState("notdone");

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Description", "Properties", "Concepts"]));
        dispatch(changeBrowserTabTitle("FEvIR CodeSystem - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setCodeSystemResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setCodeSystemResourceState(resourceJson);
            setConceptsReorganizedState({ "reorganized": false, "concept": resourceJson.concept });
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: CodeSystem " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (codeSystemResourceState?.concept?.length > 0 && emptyPropertyValuesClearedState === "notdone") {
            let newConceptArray = JSON.parse(JSON.stringify(codeSystemResourceState.concept)).map(concept => { return removeEmptyPropertyValues(concept); });
            setCodeSystemResourceState(prevState => { return { ...prevState, "concept": newConceptArray }; });
            setEmptyPropertyValuesClearedState("donebutnotapplied");
        }
    }, [emptyPropertyValuesClearedState, editMode]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(codeSystemResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = codeSystemResourceState.title;
            newResourceDetail.displayTitle = codeSystemResourceState.title ||
                codeSystemResourceState.name || newResourceDetail.displayTitle;
            if (codeSystemResourceState.title !== undefined &&
                previousFinalValue !== codeSystemResourceState.title) {
                console.log("resourceUpdate for CodeSystem.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    codeSystemResourceState.title);
            }
        }
    }, [codeSystemResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = codeSystemResourceState.name;
            newResourceDetail.displayTitle = codeSystemResourceState.title ||
                codeSystemResourceState.name || newResourceDetail.displayTitle;
            if (codeSystemResourceState.name !== undefined &&
                previousFinalValue !== codeSystemResourceState.name) {
                console.log("resourceUpdate for CodeSystem.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "name",
                    previousInitialValue,
                    codeSystemResourceState.name);
            }
        }
    }, [codeSystemResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "version", "string", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "copyright", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.copyright]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "caseSensitive", "boolean", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.caseSensitive]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "valueSet", "Reference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.valueSet]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "hierarchyMeaning", "code", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.hierarchyMeaning]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "compositional", "boolean", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.compositional]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "versionNeeded", "boolean", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.versionNeeded]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "content", "code", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.content]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "supplements", "canonical", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.supplements]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "count", "unsignedInt", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.count]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "filter", "ArrayOfCodeSystemFilter", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.filter]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(codeSystemResourceState, "property", "ArrayOfCodeSystemProperty", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [codeSystemResourceState.property]);

    useEffect(() => {
        if (editMode && (reorganizeConceptsModalState.changesMade || emptyPropertyValuesClearedState === "donebutnotapplied")) {
            if (emptyPropertyValuesClearedState === "donebutnotapplied") {
                setEmptyPropertyValuesClearedState("applied");
            }
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.concept = codeSystemResourceState.concept;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [codeSystemResourceState.concept, editMode]);

    useEffect(() => {
        if (editMode && resourceJson && conceptsReorganizedState.reorganized &&
            Array.isArray(conceptsReorganizedState.concept)) {
            setConceptsReorganizedState(prevState => { return { ...prevState, "reorganized": false } });
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.concept || null;
            previousFinalValue = newResourceDetail.revisedResourceJson.concept || null;
            newResourceDetail.revisedResourceJson.concept = conceptsReorganizedState.concept;
            if (conceptsReorganizedState.concept !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(conceptsReorganizedState.concept) &&
                !(!previousInitialValue &&
                    Array.isArray(conceptsReorganizedState.concept) &&
                    (conceptsReorganizedState.concept.length === 0 ||
                        (conceptsReorganizedState.concept.length === 1 && !conceptsReorganizedState.concept[0])
                    )
                )
            ) {
                console.log("resourceUpdate for CodeSystem.concept");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfCodeSystemConcept",
                    "concept",
                    previousInitialValue,
                    conceptsReorganizedState.concept);
            }
        }
    }, [conceptsReorganizedState]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {codeSystemResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="description">Description</h3>
                <div style={{ marginLeft: "24px" }}>
                    {
                        editMode ?
                            <>
                                <h4>Narrative Summary</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                        startCollapsed
                                        startingValue={codeSystemResourceState.text} setResourceState={setCodeSystemResourceState} />
                                </div>
                            </>
                            :
                            <>
                                {(codeSystemResourceState.text?.div && codeSystemResourceState.text.status && codeSystemResourceState.text.status !== "empty") && <>
                                    <div style={{ marginLeft: "24px" }}>
                                        <br /> <br />
                                        {(foi === "27833" || foi === "27270") &&
                                            <>
                                                <Button className="formButton" positive
                                                    content="Join Voting Group"
                                                    onClick={async () => {
                                                        let codeSystemFoi;
                                                        if (foi === "27833") {
                                                            codeSystemFoi = "27833";
                                                        } else if (foi === "27270") {
                                                            codeSystemFoi = "27270";
                                                        }
                                                        if (isLoggedIn) {
                                                            let resp = await axios({
                                                                method: 'POST',
                                                                //url: server_address + ':' + ports["master_server"] + '/',
                                                                url: fevir_node_server_address,
                                                                timeout: 8000,
                                                                data: {
                                                                    'functionid': "joinvotingpermissiongroup",
                                                                    'aboutformstateid': codeSystemFoi,
                                                                    'apiToken': apiToken
                                                                },
                                                            });
                                                            if (resp?.data?.success) {
                                                                let emailMessage = "We don't have a notification email on file for you. Please visit your user profile by clicking your name in the upper right corner and add one. Otherwise, you won't receive a notification when terms are ready for vote.";
                                                                if (notificationEmail) {
                                                                    emailMessage = `Your email on record is: ${notificationEmail} which we will use to send you notices when terms are ready for vote. If you want to change your email address used for notification, please edit your user profile by clicking your name in the upper right corner.`;
                                                                }
                                                                alert(`Welcome to the ${resp?.data?.groupname}! ${emailMessage}`);
                                                            } else {
                                                                if (resp?.data?.error) {
                                                                    alert(resp.data.error);
                                                                } else {
                                                                    alert("Joining the group was unsuccessful.");
                                                                }
                                                            }
                                                        } else {
                                                            alert("Please log in to join the group.");
                                                        }
                                                    }}
                                                /><br /><br /></>}
                                        <DisplayFromFHIRXhtml xhtml={codeSystemResourceState.text.div} />
                                        <br /> <br />
                                    </div>
                                </>}
                            </>
                    }
                    {editMode ?
                        <>
                            <StringEntry elementName='title' fieldLabel='Title'
                                startingValue={codeSystemResourceState.title}
                                setResourceState={setCodeSystemResourceState} />
                        </>
                        :
                        <>
                            {codeSystemResourceState.title && <p><b>Title: </b>{codeSystemResourceState.title}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <StringEntry elementName='name' fieldLabel='Name'
                                startingValue={codeSystemResourceState.name}
                                setResourceState={setCodeSystemResourceState} />
                        </>
                        :
                        <>
                            {codeSystemResourceState.name && <p><b>Name: </b>{codeSystemResourceState.name}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <StringEntry elementName='version' fieldLabel='Version'
                                startingValue={codeSystemResourceState.version}
                                setResourceState={setCodeSystemResourceState} />
                        </>
                        :
                        <>
                            {codeSystemResourceState.version && <p><b>Version: </b>{codeSystemResourceState.version}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='description' fieldLabel='Description'
                                startingValue={codeSystemResourceState.description}
                                setResourceState={setCodeSystemResourceState} />
                        </>
                        :
                        <>
                            {codeSystemResourceState.description &&
                                <div>
                                    <b>Description: </b><DisplayFromFHIRMarkdown markdown={codeSystemResourceState.description} />
                                    <br /><br />
                                </div>}
                        </>
                    }
                    {editMode ?
                        <>
                            <UriEntry elementName='valueSet' fieldLabel='Value set with entire code system'
                                startingValue={codeSystemResourceState.valueSet} setResourceState={setCodeSystemResourceState} />
                        </>
                        :
                        <>
                            {codeSystemResourceState.valueSet &&
                                <div>
                                    <b>Value set with entire code system: </b><DisplayFromFHIRUri uri={codeSystemResourceState.valueSet} />
                                </div>}
                        </>
                    }
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='copyright' fieldLabel='Copyright'
                                startingValue={codeSystemResourceState.copyright}
                                setResourceState={setCodeSystemResourceState} />
                        </>
                        :
                        <>
                            {codeSystemResourceState.copyright &&
                                <div>
                                    <b>Copyright: </b><DisplayFromFHIRMarkdown markdown={codeSystemResourceState.copyright} />
                                    <br /><br />
                                </div>}
                        </>
                    }
                </div>
                <h3 id="properties">Properties</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <div>
                            <BooleanEntry elementName='caseSensitive' fieldLabel='Case sensitive'
                                storeFalse={true}
                                startingValue={codeSystemResourceState.caseSensitive}
                                setResourceState={setCodeSystemResourceState} />
                            <CodeEntry elementName='hierarchyMeaning' fieldLabel='Hierarchy meaning'
                                dataEntryStyle="dropdown"
                                allowedValues={["grouped-by", "is-a", "part-of", "classified-with"]}
                                startingValue={codeSystemResourceState.hierarchyMeaning}
                                setResourceState={setCodeSystemResourceState} />
                            <BooleanEntry elementName='compositional' fieldLabel='Compositional'
                                storeFalse={true}
                                startingValue={codeSystemResourceState.compositional}
                                setResourceState={setCodeSystemResourceState} />
                            <BooleanEntry elementName='versionNeeded' fieldLabel='Version needed'
                                storeFalse={true}
                                startingValue={codeSystemResourceState.versionNeeded}
                                setResourceState={setCodeSystemResourceState} />
                            <CodeEntry elementName='content' fieldLabel='Content'
                                dataEntryStyle="dropdown"
                                allowedValues={["not-present", "example", "fragment", "complete", "supplement"]}
                                startingValue={codeSystemResourceState.content}
                                setResourceState={setCodeSystemResourceState} />
                            <ArrayEntry datatype='CodeSystemProperty'
                                elementName='property' fieldLabel='Property'
                                startingValue={codeSystemResourceState.property}
                                setResourceState={setCodeSystemResourceState} />
                        </div>
                        :
                        <>
                            {codeSystemResourceState.caseSensitive && <><b>Case sensitive: </b>{codeSystemResourceState.caseSensitive.toString()}<br /></>}
                            {codeSystemResourceState.hierarchyMeaning && <><b>Hierarchy meaning: </b>{codeSystemResourceState.hierarchyMeaning}<br /></>}
                            {codeSystemResourceState.compositional && <><b>Compositional: </b>{codeSystemResourceState.compositional.toString()}<br /></>}
                            {codeSystemResourceState.versionNeeded && <><b>Version needed: </b>{codeSystemResourceState.versionNeeded.toString()}<br /></>}
                            {codeSystemResourceState.content && <><b>Content: </b>{codeSystemResourceState.content}<br /></>}
                            <b>Properties</b>
                            <div style={{ marginLeft: "24px" }}>
                                <SimpleResourceFieldViewer resource={codeSystemResourceState.property} parentElement={""} />
                            </div>
                        </>
                    }
                </div>
                <h3 id="concepts">Concepts</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode &&
                        <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                            <ReorganizeConceptsButton conceptsReorganizedState={conceptsReorganizedState}
                                setConceptsReorganizedState={setConceptsReorganizedState}
                                reorganizeConceptsModalState={reorganizeConceptsModalState}
                                setReorganizeConceptsModalState={setReorganizeConceptsModalState} />
                        </div>
                    }
                    <SimpleResourceFieldViewer resource={codeSystemResourceState.concept} parentElement={""} />
                </div>
            </div>
        </>}
    </div>
}

export default CodeSystemDisplay;
export { CodeSystemPropertyEntry };